import {
  faAnglesUpDown,
  faArrowLeftFromBracket,
  faUser,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { router, usePage } from '@inertiajs/react';

import { resetIntercomSession } from '@/components/shared/intercom-widget';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Icon } from '@/components/ui/icon';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { cmsUserPath, destroyUserSessionPath } from '@/lib/routes';
import { formatRoleLabel, getInitials } from '@/lib/utils';
import type { CurrentUser, SharedData } from '@/types/shared';

export function UserInfo({ user, showRole = true }: { user: CurrentUser; showRole?: boolean }) {
  const initials = getInitials(user.firstName, user.lastName);
  const fullName = user?.name;
  const role = formatRoleLabel(user?.role);

  return (
    <>
      <Avatar className="h-8 w-8 rounded-full">
        <AvatarImage src={user?.avatar} alt={fullName} />
        <AvatarFallback className="rounded-full">{initials}</AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-col text-left text-sm leading-tight">
        <span className="flex min-w-0 items-center gap-2 font-medium">
          <span className="truncate">{fullName}</span>
          {showRole && (
            <Badge size="sm" className="shrink-0">
              {role}
            </Badge>
          )}
        </span>
        <span className="truncate text-xs" title={user.email}>
          {user.email}
        </span>
      </div>
    </>
  );
}

function NavUser() {
  const { currentUser } = usePage<SharedData>().props;

  const { isMobile } = useSidebar();
  const handleLogout = () => {
    resetIntercomSession();
    router.delete(destroyUserSessionPath());
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground gap-3"
            >
              <UserInfo user={currentUser} showRole={false} />
              <Icon icon={faAnglesUpDown} className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-3 px-1 py-1.5 text-left text-sm">
                <UserInfo user={currentUser} />
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => router.get(cmsUserPath(currentUser.id))}
                className="cursor-pointer"
              >
                <Icon icon={faUser} />
                Edit Profile
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
              <Icon icon={faArrowLeftFromBracket} />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export default NavUser;
